import React, { useState, useEffect } from 'react';
import axios from 'axios';
import baseUrl from '../../config';
import { online } from '../../Assets';
import { StarIcon, LockClosedIcon, ChartBarSquareIcon, GiftIcon, TrophyIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid'; // For snackbar icons

const Reward = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [redeeming, setRedeeming] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [addressLine1, setAddressLine1] = useState('');
    const [addressLine2, setAddressLine2] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [country, setCountry] = useState('India');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        type: 'success' // 'success' or 'error'
    });
    const token = localStorage.getItem('token');

    // Fetch dashboard data
    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                // Ensure baseUrl is correctly imported and accessible
                const response = await axios.get(`${baseUrl}api/students/wallet/getwallet`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setDashboardData(response.data);
                setEmail(response.data.user?.email || '');
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to fetch dashboard data');
                console.error("Error fetching dashboard data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [token, baseUrl]);

    // Handle product redemption dialog opening
    const handleRedeemClick = (product) => {
        setSelectedProduct(product);
    };

    // Handle closing the redemption dialog
    const handleCloseDialog = () => {
        setSelectedProduct(null);
        // Reset form fields
        setAddressLine1('');
        setAddressLine2('');
        setCity('');
        setState('');
        setPostalCode('');
        setCountry('India');
        setPhoneNumber('');
        // Email is pre-filled, so no need to reset unless user changes it
    };

    // Handle submitting the redemption request
    const handleRedeemSubmit = async () => {
        setRedeeming(true);
        try {
            await axios.post(`${baseUrl}api/rewards/redeem`, {
                productId: selectedProduct._id,
                studentsDetails: {
                    fullName: dashboardData?.userName,
                    addressLine1,
                    addressLine2,
                    city,
                    state,
                    postalCode,
                    country,
                    phoneNumber,
                    email
                }
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            // Refresh dashboard data after successful redemption
            const response = await axios.get(`${baseUrl}api/students/wallet/getwallet`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setDashboardData(response.data);

            setSnackbar({
                open: true,
                message: 'Redemption request submitted for admin approval!',
                type: 'success'
            });
            handleCloseDialog();
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.message || 'Redemption failed',
                type: 'error'
            });
            console.error("Redemption error:", err);
        } finally {
            setRedeeming(false);
        }
    };

    // Handle closing the snackbar notification
    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Determine reward status based on user's points
    const getRewardStatus = (pointsRequired) => {
        if (!dashboardData) return 'locked';
        const userPoints = dashboardData.balance || 0;
        return userPoints >= pointsRequired ? 'available' : 'locked';
    };

    // Default rewards data (mock data for display)
    const defaultRewards = [
        { _id: '1', name: 'Shoutout on Oratrics Page', pointsRequired: 150, image: online },
        { _id: '2', name: 'Custom Oratrics Badge', pointsRequired: 200, image: online },
        { _id: '3', name: 'Get 5 extra classes', pointsRequired: 250, image: online },
        { _id: '4', name: 'Get 5 Maths classes', pointsRequired: 350, image: online },
        { _id: '5', name: 'Amazon gift card ($50)', pointsRequired: 500, image: online },
        { _id: '6', name: 'Oratrics Goodie Bag', pointsRequired: 600, image: online },
        { _id: '7', name: 'RGB Desk Lamp', pointsRequired: 750, image: online },
        { _id: '8', name: 'Amazon Echo Dot', pointsRequired: 1000, image: online },
        { _id: '9', name: 'Amazon Kindle', pointsRequired: 1500, image: online },
        { _id: '10', name: 'Apple AirPods Pro', pointsRequired: 3000, image: online },
        { _id: '11', name: 'Sony PS5', pointsRequired: 4500, image: online },
        { _id: '12', name: 'Apple MacBook Air', pointsRequired: 10000, image: online },
    ];

    // Actions for earning points
    const earnActions = [
        { text: 'Complete a course module', points: 10 },
        { text: 'Win a weekly speech challenge', points: 20 },
        { text: 'Share a speech on Social Media with #oratrics', points: 50 },
        { text: 'Get a friend to attend a free trial class', points: 100 },
    ];

    // Loading state UI
   if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 font-sans p-6">
        <div className="flex flex-col items-center p-8 bg-white rounded-3xl shadow-xl">
          <div className="animate-bounce bg-blue-500 p-4 rounded-full mb-4">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>
          </div>
          <p className="text-xl text-gray-700 font-semibold">Loading reward data...</p>
        </div>
      </div>
    );
  }

    // Error state UI
    if (error) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-red-50">
                <p className="text-xl text-red-600 font-semibold">Error: {error}</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6 lg:p-8 font-inter">
            {/* Header Section */}
            <div className="max-w-6xl mx-auto mb-3">
                <h1 className="text-2xl sm:text-5xm font-extrabold text-gray-900 leading-tight mb-2">
                    Welcome, <span className="text-blue-600">{dashboardData?.userName || 'Student'}!</span>
                </h1>
                <p className="text-md sm:text-md text-gray-600 font-medium">
                    Your journey to amazing rewards starts here. Keep learning, keep earning!
                </p>
            </div>

            {/* Rewards Hub Banner */}
            <div className="max-w-6xl mx-auto bg-gradient-to-r from-yellow-400 to-amber-500 p-5 sm:p-3 mb-8 rounded-xl shadow-lg flex items-center justify-between text-gray-900">
                <h2 className="text-1xl sm:text-2xl font-bold flex items-center">
                    <TrophyIcon className="w-8 h-8 sm:w-10 sm:h-10 mr-3 text-white drop-shadow-md" />
                    Your Rewards Hub
                </h2>
                <button className="bg-white text-yellow-700 px-4 py-2 rounded-full font-semibold text-sm hover:bg-yellow-50 transition-colors shadow-md">
                    How it Works
                </button>
            </div>

            <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                {/* Current Points Card */}
                <div className="md:col-span-1">
                    <div className="bg-white rounded-xl shadow-lg p-6 text-center flex flex-col items-center justify-center h-full transform transition-transform duration-300 hover:scale-[1.02] hover:shadow-xl border border-blue-100">
                        <div className="bg-blue-100 p-4 rounded-full mb-4">
                            <StarIcon className="w-12 h-12 text-blue-500" />
                        </div>
                        <p className="text-6xl font-extrabold text-blue-600 leading-none mb-2">
                            {dashboardData?.balance || 0}
                        </p>
                        <p className="text-xl font-semibold text-gray-700 mb-4">Total Points</p>
                        <p className="text-sm text-gray-500 leading-relaxed max-w-xs">
                            You're doing great! Keep collecting points for exclusive rewards.
                        </p>
                    </div>
                </div>

                {/* Earn Points Actions Card */}
                <div className="md:col-span-2">
                    <div className="bg-white rounded-xl shadow-lg p-6 h-full transform transition-transform duration-300 hover:scale-[1.01] hover:shadow-xl border border-green-100">
                        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
                            <ChartBarSquareIcon className="w-7 h-7 text-green-500 mr-2" />
                            Ways to Earn Points
                        </h3>
                        <div className="space-y-4">
                            {earnActions.map((item, index) => (
                                <div key={index} className="flex items-center justify-between p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors duration-200 border border-gray-200">
                                    <div className="flex items-center">
                                        <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center text-sm font-bold mr-4 shadow-md">
                                            +{item.points}
                                        </div>
                                        <p className="text-lg text-gray-700 font-medium">{item.text}</p>
                                    </div>
                                    <button
                                        className="bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold hover:bg-green-200 transition-colors shadow-sm"
                                    >
                                        Earn Now
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button
                            className="w-full mt-6 py-3 rounded-lg border-2 border-gray-300 text-gray-700 font-bold text-md hover:bg-gray-100 transition-colors shadow-sm"
                        >
                            View All Actions
                        </button>
                    </div>
                </div>
            </div>

            {/* Available Rewards Section */}
            <div className="max-w-6xl mx-auto mb-8">
                <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6 flex items-center">
                    <GiftIcon className="w-9 h-9 text-purple-600 mr-3" />
                    Unlock Amazing Rewards
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {defaultRewards.map((reward) => {
                        const status = getRewardStatus(reward.pointsRequired);
                        const userPoints = dashboardData?.balance || 0;
                        const progress = Math.min(100, (userPoints / reward.pointsRequired) * 100);

                        return (
                            <div key={reward._id} className={`bg-white rounded-xl shadow-lg overflow-hidden flex flex-col transform transition-all duration-300 ${status === 'locked' ? 'opacity-70 grayscale cursor-not-allowed' : 'hover:scale-[1.03] hover:shadow-xl'}`}>
                                <div className="relative h-48 w-full overflow-hidden">
                                    <img
                                        src={reward.image}
                                        alt={reward.name}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold flex items-center shadow-md
                                        ${status === 'locked' ? 'bg-gray-700 text-white' : 'bg-green-500 text-white'}`}>
                                        {status === 'locked' ? (
                                            <>
                                                <LockClosedIcon className="w-3 h-3 mr-1" /> Locked
                                            </>
                                        ) : (
                                            'Unlocked'
                                        )}
                                    </div>
                                </div>
                                <div className="p-5 flex flex-col flex-grow">
                                    <h4 className="text-xl font-bold text-gray-800 mb-2 leading-tight">
                                        {reward.name}
                                    </h4>
                                    <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                                        <div
                                            className="h-2.5 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 transition-all duration-500 ease-out"
                                            style={{ width: `${progress}%` }}
                                        ></div>
                                    </div>
                                    <div className="flex justify-between items-center text-sm mb-4">
                                        <p className="font-bold text-yellow-700">{reward.pointsRequired} Points</p>
                                        <p className="text-gray-500">
                                            {status === 'locked' ? `${reward.pointsRequired - userPoints} points more` : 'Ready!'}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => handleRedeemClick(reward)}
                                        className={`w-full py-3 rounded-lg text-lg font-semibold transition-all duration-200 shadow-md
                                            ${status === 'locked'
                                                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                                                : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                                            }`}
                                        disabled={status === 'locked'}
                                    >
                                        {status === 'locked' ? 'Locked' : 'Redeem Now'}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Redemption Dialog (Custom Modal) */}
            {selectedProduct && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 animate-fade-in">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl transform scale-95 opacity-0 animate-scale-in-up">
                        <div className="flex justify-between items-center p-6 border-b border-gray-200">
                            <h3 className="text-2xl font-bold text-gray-800">Redeem {selectedProduct.name}</h3>
                            <button onClick={handleCloseDialog} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <XMarkIcon className="w-7 h-7" />
                            </button>
                        </div>
                        <div className="p-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
                            <h4 className="text-xl font-semibold text-gray-700 mb-5">Shipping Information</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 mb-6">
                                <div>
                                    <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                                    <input
                                        type="text"
                                        id="fullName"
                                        className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed focus:outline-none"
                                        value={dashboardData?.userName || ''}
                                        disabled
                                    />
                                </div>
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        id="email"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="col-span-full">
                                    <label htmlFor="address1" className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                                    <input
                                        type="text"
                                        id="address1"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
                                        value={addressLine1}
                                        onChange={(e) => setAddressLine1(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="col-span-full">
                                    <label htmlFor="address2" className="block text-sm font-medium text-gray-700 mb-1">Address Line 2 (Optional)</label>
                                    <input
                                        type="text"
                                        id="address2"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
                                        value={addressLine2}
                                        onChange={(e) => setAddressLine2(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">City</label>
                                    <input
                                        type="text"
                                        id="city"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
                                    <input
                                        type="text"
                                        id="state"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
                                        value={state}
                                        onChange={(e) => setState(e.target.value)}
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                                    <input
                                        type="text"
                                        id="postalCode"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
                                        value={postalCode}
                                        onChange={(e) => setPostalCode(e.target.value)}
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                                    <input
                                        type="text"
                                        id="country"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
                                        value={country}
                                        onChange={(e) => setCountry(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="col-span-full">
                                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                                    <input
                                        type="tel"
                                        id="phoneNumber"
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="bg-blue-50 p-5 rounded-lg border border-blue-200">
                                <h4 className="text-xl font-semibold text-blue-800 mb-3">Order Summary</h4>
                                <div className="flex justify-between items-center mb-2">
                                    <p className="text-gray-700">Product:</p>
                                    <p className="font-bold text-blue-700">{selectedProduct?.name}</p>
                                </div>
                                <div className="flex justify-between items-center mb-2">
                                    <p className="text-gray-700">Points Required:</p>
                                    <p className="font-bold text-blue-700">{selectedProduct?.pointsRequired}</p>
                                </div>
                                <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                                    <p className="text-gray-700">Your Current Balance:</p>
                                    <p className="font-bold text-blue-700">{dashboardData?.balance}</p>
                                </div>
                            </div>
                        </div>
                        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
                            <button
                                onClick={handleCloseDialog}
                                className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-semibold hover:bg-gray-100 transition-colors shadow-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRedeemSubmit}
                                className={`px-6 py-3 rounded-lg text-white font-semibold transition-all duration-200 shadow-md flex items-center justify-center
                                    ${redeeming || !addressLine1 || !city || !state || !postalCode || !country || !phoneNumber || !email
                                        ? 'bg-blue-300 cursor-not-allowed'
                                        : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                                    }`}
                                disabled={redeeming || !addressLine1 || !city || !state || !postalCode || !country || !phoneNumber || !email}
                            >
                                {redeeming ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                        Submitting...
                                    </>
                                ) : (
                                    'Submit Redemption Request'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Snackbar (Custom Notification Toast) */}
            {snackbar.open && (
                <div
                    className={`fixed bottom-6 right-6 p-4 rounded-lg shadow-xl text-white flex items-center space-x-3 transition-all duration-300 transform ${snackbar.type === 'success' ? 'bg-green-500' : 'bg-red-500'} animate-slide-in-right z-50`}
                    role="alert"
                >
                    {snackbar.type === 'success' ? (
                        <CheckCircleIcon className="w-6 h-6" />
                    ) : (
                        <ExclamationCircleIcon className="w-6 h-6" />
                    )}
                    <span className="font-medium">{snackbar.message}</span>
                    <button onClick={handleCloseSnackbar} className="ml-4 text-white hover:text-gray-200 transition-colors">
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default Reward;

